import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const SAMPLE_HTML = `<h1>Contrato laboral</h1>
<p>Patrón: {{nombre_patron}} — Representante: {{representante_legal}}</p>
<p>Trabajador: {{nombre_trabajador}} — Puesto: {{puesto}}</p>
<p>Salario: {{salario}} — Periodo: {{periodo}} — Inicio: {{fecha_inicio}}</p>`;

/**
 * Diseñador de plantillas de contrato (HTML/DOCX referenciado en content_ref).
 * Campos dinámicos con sintaxis {{campo}}.
 */
export default function ContractDesignerPanel({ clientId }) {
  const [catalog, setCatalog] = useState([]);
  const [name, setName] = useState("Plantilla personalizada");
  const [html, setHtml] = useState(SAMPLE_HTML);
  const [designs, setDesigns] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const [catRes, listRes] = await Promise.all([
        api.get("/laboral/contratos/designer/catalogo-campos"),
        api.get("/laboral/contratos/designer/plantillas", { params: { client_id: clientId } }),
      ]);
      setCatalog(catRes.data?.fields || []);
      setDesigns(listRes.data?.items || []);
    } catch (e) {
      toast.error("No se pudo cargar el diseñador de contratos");
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const detectedFields = (html.match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g) || [])
    .map((m) => m.replace(/\{\{|\}\}/g, "").trim());

  const saveDesign = async () => {
    if (!clientId) return;
    setSaving(true);
    try {
      await api.post("/laboral/contratos/designer/plantillas", {
        client_id: clientId,
        name,
        format: "html",
        content_ref: html,
        marked_fields: [...new Set(detectedFields)],
        active: true,
      });
      toast.success("Plantilla guardada");
      load();
    } catch (e) {
      toast.error("Error al guardar plantilla");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Diseñador de contratos</h3>
        <p className="text-xs text-slate-600 mt-1">
          Cargue plantilla HTML o referencie DOCX en el servidor. Use marcadores como{" "}
          <code className="bg-slate-100 px-1">{`{{nombre_trabajador}}`}</code>.
        </p>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="design-name">Nombre de plantilla</Label>
        <Input id="design-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="design-html">Contenido HTML</Label>
        <Textarea
          id="design-html"
          spellCheck={false}
          className="min-h-[180px] font-mono text-xs"
          value={html}
          onChange={(e) => setHtml(e.target.value)}
        />
      </div>
      <div className="text-xs text-slate-600">
        <p className="font-medium mb-1">Campos detectados:</p>
        <p>{detectedFields.length ? detectedFields.join(", ") : "Ninguno"}</p>
      </div>
      <div className="text-xs text-slate-600">
        <p className="font-medium mb-1">Catálogo de campos:</p>
        <ul className="list-disc pl-4">
          {catalog.map((f) => (
            <li key={f.key}>
              <code>{`{{${f.key}}}`}</code> — {f.label}
            </li>
          ))}
        </ul>
      </div>
      <Button type="button" onClick={saveDesign} disabled={saving || !clientId}>
        {saving ? "Guardando…" : "Guardar plantilla activa"}
      </Button>
      {designs.length > 0 && (
        <div className="text-xs text-slate-500">
          Plantillas registradas: {designs.length}
        </div>
      )}
    </div>
  );
}
